import { useState } from 'react';
import { Copy, Check } from 'lucide-react';
import { motion } from 'framer-motion';
import { copyRich, copyPlain } from '../../lib/clipboard';

interface CopyButtonProps {
  text: string;
  /** Optional HTML variant — when provided, clipboard gets rich + plain text */
  html?: string;
  label?: string;
  variant?: 'default' | 'small' | 'icon';
  className?: string;
}

export function CopyButton({
  text,
  html,
  label = 'Copy',
  variant = 'default',
  className = '',
}: CopyButtonProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    const ok = html ? await copyRich({ html, text }) : await copyPlain(text);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (variant === 'icon') {
    return (
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={handleCopy}
        className={`p-2 rounded-lg bg-slate-700/50 hover:bg-slate-600/50 transition-colors ${className}`}
        title={copied ? 'Copied!' : label}
      >
        {copied ? (
          <Check className="w-4 h-4 text-green-400" />
        ) : (
          <Copy className="w-4 h-4 text-slate-400" />
        )}
      </motion.button>
    );
  }

  const sizeClasses =
    variant === 'small' ? 'px-2 py-1 text-xs gap-1' : 'px-3 py-1.5 text-sm gap-2';

  return (
    <motion.button
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={handleCopy}
      className={`flex items-center ${sizeClasses} rounded-lg font-medium transition-all ${
        copied
          ? 'bg-green-500/20 text-green-400 border border-green-500/30'
          : 'bg-slate-700/50 text-slate-300 hover:bg-slate-600/50 border border-slate-600/50'
      } ${className}`}
    >
      {copied ? (
        <>
          <Check className={variant === 'small' ? 'w-3 h-3' : 'w-4 h-4'} />
          Copied!
        </>
      ) : (
        <>
          <Copy className={variant === 'small' ? 'w-3 h-3' : 'w-4 h-4'} />
          {label}
        </>
      )}
    </motion.button>
  );
}
